// Canonical per-vehicle-type mode catalogs used by the renderer (mirrors
// `src/main/vehicle.js`). Keys are vehicle-type names as returned by
// `Vehicle.getMavTypeName()` (Copter / Plane / Rover / Sub / Tracker).
// QuadPlane shares Plane's full mode list, so we normalize to "Plane".

import store from './storage';

export const MODE_CATALOGS = {
    Copter: {
        0: 'Stabilize',
        1: 'Acro',
        2: 'Alt Hold',
        3: 'Auto',
        4: 'Guided',
        5: 'Loiter',
        6: 'RTL',
        7: 'Circle',
        9: 'Land',
        11: 'Drift',
        13: 'Sport',
        14: 'Flip',
        15: 'Autotune',
        16: 'PosHold',
        17: 'Brake',
        18: 'Throw',
        19: 'Avoid ADSB',
        20: 'Guided No GPS',
        21: 'Smart RTL',
        22: 'FlowHold',
        23: 'Follow',
        24: 'ZigZag',
        25: 'System ID',
        26: 'Autorotate',
        27: 'Auto RTL',
        28: 'Turtle',
    },
    Plane: {
        0: 'Manual',
        1: 'Circle',
        2: 'Stabilize',
        3: 'Training',
        4: 'Acro',
        5: 'FBWA',
        6: 'FBWB',
        7: 'Cruise',
        8: 'Autotune',
        10: 'Auto',
        11: 'RTL',
        12: 'Loiter',
        13: 'Takeoff',
        14: 'Avoid ADSB',
        15: 'Guided',
        16: 'Initialising',
        17: 'QStabilize',
        18: 'QHover',
        19: 'QLoiter',
        20: 'QLand',
        21: 'QRTL',
        22: 'QAutotune',
        23: 'QAcro',
        24: 'Thermal',
        25: 'Loiter Alt QLand',
        26: 'Autoland',
    },
    Rover: {
        0: 'Manual',
        1: 'Acro',
        3: 'Steering',
        4: 'Hold',
        5: 'Loiter',
        6: 'Follow',
        7: 'Simple',
        8: 'Dock',
        9: 'Circle',
        10: 'Auto',
        11: 'RTL',
        12: 'Smart RTL',
        15: 'Guided',
        16: 'Initialising',
    },
    Sub: {
        0: 'Stabilize',
        1: 'Acro',
        2: 'Alt Hold',
        3: 'Auto',
        4: 'Guided',
        7: 'Circle',
        9: 'Surface',
        16: 'PosHold',
        19: 'Manual',
        20: 'Motor Detect',
        21: 'SurfTrak',
    },
    Tracker: {
        0: 'Manual',
        1: 'Stop',
        2: 'Scan',
        3: 'ServoTest',
        10: 'Auto',
        16: 'Initialising',
    },
};

export const MODE_TYPES = ['Copter', 'Plane', 'Rover', 'Sub', 'Tracker'];

export const STORE_KEY = 'modeListFilter';
export const CHANGE_EVENT = 'mode-list-filter-changed';

// Normalize vehicle-type names returned by the main process to a catalog key.
export function normalizeVehicleType(name) {
    if (name === 'QuadPlane') return 'Plane';
    if (MODE_TYPES.includes(name)) return name;
    return null;
}

// Returns { Copter: { '0': true, ... }, Plane: {...}, ... } — undefined entry
// means "no preference saved" → caller should treat as all-enabled.
export function loadModeFilter() {
    const raw = store.get(STORE_KEY, {});
    return raw && typeof raw === 'object' ? raw : {};
}

export function saveModeFilter(filter) {
    store.set(STORE_KEY, filter);
    try {
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { filter } }));
    } catch (_) { /* noop */ }
}

// Given a runtime modeList (numeric-id → label map from the per-vehicle
// firmware), return a new map containing only the modes the user has enabled
// for `vehicleTypeName`. If no preference is saved, returns modeList unchanged.
export function filterModeList(modeList, vehicleTypeName, filter) {
    if (!modeList) return {};
    const key = normalizeVehicleType(vehicleTypeName);
    if (!key) return modeList;
    const f = filter || loadModeFilter();
    const enabled = f[key];
    if (!enabled) return modeList;
    const out = {};
    Object.keys(modeList).forEach((id) => {
        if (enabled[id]) out[id] = modeList[id];
    });
    // If user disabled everything, fall back to full list to avoid an empty
    // selector that would lock the user out of mode changes.
    return Object.keys(out).length > 0 ? out : modeList;
}
